import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Tabs,
  Tab,
  Grid,
  Chip,
  Alert,
  AlertTitle,
  Paper,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Storage as StorageIcon,
  BarChart as BarChartIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
} from "@mui/icons-material";

const KVStorageCleanupPanel = () => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [lastCleanup, setLastCleanup] = useState(null);
  const [cleanupHistory, setCleanupHistory] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  // Form states
  const [orderNumber, setOrderNumber] = useState("");
  const [symphonyCookie, setSymphonyCookie] = useState("");
  const [expiredHours, setExpiredHours] = useState(48);

  // Mock API call - replace with your actual API client
  const apiCall = useCallback(async (endpoint, data = null, method = "GET") => {
    setLoading(true);
    try {
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Return mock data based on endpoint
      return mockApiResponse(endpoint, data);
    } finally {
      setLoading(false);
    }
  }, []);

  // Mock API responses
  const mockApiResponse = (endpoint, data) => {
    switch (endpoint) {
      case "cleanup-statistics":
        return {
          success: true,
          statistics: {
            total: 1247,
            categories: {
              orders: 203,
              fraudWarnings: 45,
              symphonySessions: 178,
              userSessions: 312,
              guestSessions: 89,
              securityEntries: 420,
            },
            storageUsed: 2548000,
            healthScore: 73,
            lastUpdated: new Date().toISOString(),
            potentialSavings: 871480,
          },
        };

      case "cleanup-preview":
        return {
          success: true,
          preview: {
            willDelete: Math.floor(Math.random() * 500) + 100,
            willPreserve: Math.floor(Math.random() * 300) + 200,
            estimatedSavings: Math.floor(Math.random() * 1000000) + 500000,
            estimatedSavingsFormatted: "1.2 MB",
            details: {
              willDelete: [
                {
                  key: "expired_session_abc123",
                  reason: "Expired 3 days ago",
                  size: 2048,
                },
                {
                  key: "temp_fraud_xyz789",
                  reason: "Temporary entry older than 48h",
                  size: 1024,
                },
                {
                  key: "guest_session_def456",
                  reason: "Guest session expired",
                  size: 3072,
                },
              ],
              willPreserve: [
                {
                  key: "SONW_order_12345",
                  reason: "Active order entry",
                  size: 4096,
                },
                {
                  key: "fraud_warning_active",
                  reason: "Recent fraud warning",
                  size: 2048,
                },
                {
                  key: "user_session_current",
                  reason: "Active user session",
                  size: 1536,
                },
              ],
            },
          },
          safetyAnalysis: {
            dashboardImpact: "None",
            userContinuationImpact: "None",
            reversibility: "Cannot be undone",
            confidence: "high",
          },
        };

      case "execute-cleanup":
        return {
          success: true,
          results: {
            deletedEntries: Math.floor(Math.random() * 400) + 100,
            preservedEntries: Math.floor(Math.random() * 300) + 200,
            storageSaved: Math.floor(Math.random() * 1000000) + 500000,
            storageSavedFormatted: "1.1 MB",
            operation: data?.orderNumber ? "order_cleanup" : "general_cleanup",
            timestamp: new Date().toISOString(),
            orderNumber: data?.orderNumber || null,
          },
        };

      case "cleanup-history":
        return {
          success: true,
          history: [
            {
              timestamp: new Date(Date.now() - 3600000).toISOString(),
              operation: "general_cleanup",
              deletedEntries: 342,
              storageSaved: "890 KB",
              success: true,
            },
            {
              timestamp: new Date(Date.now() - 7200000).toISOString(),
              operation: "order_cleanup",
              orderNumber: "SONW12345",
              deletedEntries: 156,
              storageSaved: "423 KB",
              success: true,
            },
          ],
        };

      default:
        return { success: false, error: "Unknown endpoint" };
    }
  };

  const refreshStatistics = useCallback(async () => {
    const result = await apiCall("cleanup-statistics");
    if (result.success) {
      setStatistics(result.statistics);
    }
  }, [apiCall]);

  const loadCleanupHistory = useCallback(async () => {
    const result = await apiCall("cleanup-history");
    if (result.success) {
      setCleanupHistory(result.history);
    }
  }, [apiCall]);

  // Load initial data
  useEffect(() => {
    refreshStatistics();
    loadCleanupHistory();
  }, [refreshStatistics, loadCleanupHistory]);

  const handleCleanup = async (preview = true) => {
    const data = {
      preview,
      orderNumber: orderNumber || null,
      symphonyCookie: symphonyCookie || null,
      expiredHours: parseInt(expiredHours),
    };

    const endpoint = preview ? "cleanup-preview" : "execute-cleanup";
    const result = await apiCall(endpoint, data, "POST");

    if (result.success) {
      if (preview) {
        setPreviewData(result);
      } else {
        setLastCleanup(result.results);
        setPreviewData(null);
        await refreshStatistics();
        await loadCleanupHistory();
      }
    }
  };

  const getHealthScoreColor = (score) => {
    if (score >= 80) return "success";
    if (score >= 60) return "warning";
    return "error";
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / 1048576).toFixed(1)}MB`;
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffHours = Math.round((now - time) / (1000 * 60 * 60));

    if (diffHours < 1) return "Just now";
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.round(diffHours / 24)}d ago`;
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (!statistics) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
          px: 3,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", px: 3, py: 3 }}>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <StorageIcon sx={{ mr: 1, color: "primary.main" }} />
              <Typography variant="h4" component="h1">
                KV Storage Cleanup Manager
              </Typography>
            </Box>
            <Chip
              label={`Health Score: ${statistics.healthScore}/100`}
              color={getHealthScoreColor(statistics.healthScore)}
              variant="outlined"
            />
          </Box>

          {/* Tabs */}
          <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
            <Tab icon={<BarChartIcon />} label="Overview" />
            <Tab icon={<DeleteIcon />} label="Cleanup Tools" />
            <Tab icon={<HistoryIcon />} label="History" />
          </Tabs>

          {/* Tab Content */}
          {activeTab === 0 && (
            <Box>
              {/* Statistics Grid */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Storage Overview
                    </Typography>
                    <Typography variant="h4" color="primary.main" gutterBottom>
                      {statistics.total}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Total Entries
                    </Typography>
                    <Typography variant="body1">
                      {formatFileSize(statistics.storageUsed)} Storage Used
                    </Typography>
                    <Typography variant="body1" color="success.main">
                      {formatFileSize(statistics.potentialSavings)} Potential
                      Savings
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Storage Categories
                    </Typography>
                    <List dense>
                      {Object.entries(statistics.categories).map(
                        ([category, count]) => (
                          <ListItem key={category}>
                            <ListItemText
                              primary={category
                                .replace(/([A-Z])/g, " $1")
                                .toLowerCase()
                                .replace(/^./, (str) => str.toUpperCase())}
                              secondary={`${count} entries`}
                            />
                          </ListItem>
                        )
                      )}
                    </List>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              {/* Cleanup Forms */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Order-Specific Cleanup
                    </Typography>
                    <TextField
                      fullWidth
                      label="Order Number"
                      value={orderNumber}
                      onChange={(e) => setOrderNumber(e.target.value)}
                      placeholder="e.g., SONW12345"
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="Symphony Cookie"
                      value={symphonyCookie}
                      onChange={(e) => setSymphonyCookie(e.target.value)}
                      placeholder="Optional: specific cookie to clean"
                      sx={{ mb: 2 }}
                    />
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      General Cleanup
                    </Typography>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Expired Hours Threshold</InputLabel>
                      <Select
                        value={expiredHours}
                        onChange={(e) => setExpiredHours(e.target.value)}
                        label="Expired Hours Threshold"
                      >
                        <MenuItem value={24}>24 hours</MenuItem>
                        <MenuItem value={48}>48 hours</MenuItem>
                        <MenuItem value={72}>72 hours</MenuItem>
                        <MenuItem value={168}>1 week</MenuItem>
                      </Select>
                    </FormControl>
                    <Typography variant="body2" color="text.secondary">
                      Entries older than this threshold will be considered for
                      cleanup
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Action Buttons */}
              <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={() => handleCleanup(true)}
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Preview Cleanup"}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleCleanup(false)}
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Execute Cleanup"}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={refreshStatistics}
                  disabled={loading}
                >
                  Refresh Stats
                </Button>
              </Box>

              {/* Preview Results */}
              {previewData && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <AlertTitle>
                    Cleanup Preview -{" "}
                    {previewData.preview.estimatedSavingsFormatted} Potential
                    Savings
                  </AlertTitle>

                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={6}>
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography color="error.main">
                            Will Delete (
                            {previewData.preview.details.willDelete.length}{" "}
                            items)
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <List dense>
                            {previewData.preview.details.willDelete.map(
                              (entry, idx) => (
                                <ListItem key={idx}>
                                  <ListItemText
                                    primary={
                                      <Typography
                                        variant="body2"
                                        fontFamily="monospace"
                                      >
                                        {entry.key}
                                      </Typography>
                                    }
                                    secondary={
                                      <Box>
                                        <Typography
                                          variant="caption"
                                          display="block"
                                        >
                                          {entry.reason}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="primary.main"
                                        >
                                          {formatFileSize(entry.size)}
                                        </Typography>
                                      </Box>
                                    }
                                  />
                                </ListItem>
                              )
                            )}
                          </List>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography color="success.main">
                            Will Preserve (
                            {previewData.preview.details.willPreserve.length}{" "}
                            items)
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <List dense>
                            {previewData.preview.details.willPreserve.map(
                              (entry, idx) => (
                                <ListItem key={idx}>
                                  <ListItemText
                                    primary={
                                      <Typography
                                        variant="body2"
                                        fontFamily="monospace"
                                      >
                                        {entry.key}
                                      </Typography>
                                    }
                                    secondary={
                                      <Box>
                                        <Typography
                                          variant="caption"
                                          display="block"
                                        >
                                          {entry.reason}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="primary.main"
                                        >
                                          {formatFileSize(entry.size)}
                                        </Typography>
                                      </Box>
                                    }
                                  />
                                </ListItem>
                              )
                            )}
                          </List>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  </Grid>

                  <Paper sx={{ p: 2, mt: 2, bgcolor: "background.default" }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Safety Analysis
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" display="block">
                          Dashboard Impact:
                        </Typography>
                        <Typography variant="body2" color="success.main">
                          {previewData.safetyAnalysis.dashboardImpact}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" display="block">
                          User Impact:
                        </Typography>
                        <Typography variant="body2" color="success.main">
                          {previewData.safetyAnalysis.userContinuationImpact}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" display="block">
                          Reversibility:
                        </Typography>
                        <Typography variant="body2" color="error.main">
                          {previewData.safetyAnalysis.reversibility}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" display="block">
                          Confidence:
                        </Typography>
                        <Typography
                          variant="body2"
                          color="success.main"
                          sx={{ textTransform: "capitalize" }}
                        >
                          {previewData.safetyAnalysis.confidence}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Alert>
              )}

              {/* Last Cleanup Results */}
              {lastCleanup && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  <AlertTitle>Cleanup Completed Successfully</AlertTitle>
                  <Typography>
                    Deleted {lastCleanup.deletedEntries} entries, preserved{" "}
                    {lastCleanup.preservedEntries} entries. Saved{" "}
                    {lastCleanup.storageSavedFormatted} of storage space.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}

          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Cleanup History
              </Typography>
              {cleanupHistory.length > 0 ? (
                <Stack spacing={2}>
                  {cleanupHistory.map((item, idx) => (
                    <Paper
                      key={idx}
                      sx={{ p: 2, borderLeft: 4, borderColor: "primary.main" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{ textTransform: "capitalize" }}
                          >
                            {item.operation.replace("_", " ")}
                          </Typography>
                          {item.orderNumber && (
                            <Typography variant="body2" color="text.secondary">
                              Order: {item.orderNumber}
                            </Typography>
                          )}
                          <Typography variant="body2" color="text.secondary">
                            {item.deletedEntries} entries deleted •{" "}
                            {item.storageSaved} saved
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            {formatTimeAgo(item.timestamp)}
                          </Typography>
                          {item.success ? (
                            <CheckCircleIcon
                              sx={{ color: "success.main", fontSize: 20 }}
                            />
                          ) : (
                            <WarningIcon
                              sx={{ color: "error.main", fontSize: 20 }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Paper sx={{ p: 4, textAlign: "center" }}>
                  <Typography color="text.secondary">
                    No cleanup history available
                  </Typography>
                </Paper>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Info Panel */}
      <Alert severity="info" icon={<InfoIcon />}>
        <AlertTitle>Important Information</AlertTitle>
        <List dense>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon color="success" fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Dashboard Safe: Main order entries (SONW*) are always preserved" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon color="success" fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="User Continuation: Recent sessions preserved for checkout completion" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <InfoIcon color="info" fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Typical Savings: 60-70% storage reduction with zero functionality impact" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <WarningIcon color="warning" fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Reversibility: Cleanup operations cannot be undone - always preview first" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <BarChartIcon color="primary" fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Health Score: Based on redundant data ratio and total storage efficiency" />
          </ListItem>
        </List>
      </Alert>
    </Box>
  );
};

export default KVStorageCleanupPanel;